import { useEffect, useState } from "react";
import { Loader2, Send, ShieldCheck, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { opsApi } from "../lib/api";

/** OPS-3 email framework, front end. Every message type in the system comes through here:
 *  the draft is pre-filled from the template and the resolved contact, a person reads it, and
 *  only then does it go. The identity line says exactly how the GC will see it. */
export default function FrameworkEmailDialog({ projectId, draft, onClose, onSent }) {
  const [to, setTo] = useState((draft.to || []).join(", "));
  const [cc, setCc] = useState((draft.cc || []).join(", "));
  const [bcc, setBcc] = useState((draft.bcc || []).join(", "));
  const [subject, setSubject] = useState(draft.subject || "");
  const [body, setBody] = useState(draft.body || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [busy, onClose]);

  const send = async () => {
    const recipients = to.split(",").map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) { toast.error("Add at least one recipient"); return; }
    setBusy(true);
    try {
      const res = await opsApi.emailSend(projectId, {
        template: draft.template, to: recipients,
        cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
        bcc: bcc.split(",").map((s) => s.trim()).filter(Boolean),
        subject, body, attachments: draft.attachments || [], ref: draft.ref || {},
      });
      toast.success(`Sent to ${res.sent.join(", ")}`);
      onSent?.(res);
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.detail || "The email could not be sent");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
         onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-border bg-card text-card-foreground p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-framework-email-dialog">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900">{draft.template_name}</h3>
            <p className="mt-1 text-xs text-slate-500">
              To the {draft.audience_label?.toLowerCase()} · template v{draft.template_version}
            </p>
          </div>
          {draft.review_before_send && (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700"
                  data-testid="ops-email-review-flag">
              <ShieldCheck className="h-3 w-3" /> Review before send
            </span>
          )}
        </div>

        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
             data-testid="ops-email-identity">
          From <span className="font-medium text-slate-900">{draft.from_name} &lt;{draft.from_email}&gt;</span>
          {" · "}replies to <span className="font-medium text-slate-900">{draft.reply_to}</span>
          {draft.mechanism === "department_with_reply_to" && " (department address — you are on Reply-To)"}
        </div>

        <label className="mt-3 block text-xs font-medium text-slate-500">To</label>
        <Input value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-9 text-sm"
               data-testid="ops-email-to" />
        {!draft.to?.length && (
          <p className="mt-1 text-xs text-amber-600">
            No contact of that role was found on this job — type the address, and add the
            contact to the customer record so next time it fills itself in.
          </p>
        )}
        {draft.candidates?.length > 1 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {draft.candidates.slice(0, 8).map((c) => (
              <button key={c.email} onClick={() => setTo(c.email)}
                      data-testid={`ops-email-candidate-${c.email}`}
                      className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground hover:border-slate-400">
                {c.name || c.email}{c.title ? ` · ${c.title}` : ""}
              </button>
            ))}
          </div>
        )}

        <label className="mt-3 block text-xs font-medium text-slate-500">Cc</label>
        <Input value={cc} onChange={(e) => setCc(e.target.value)} className="mt-1 h-9 text-sm"
               data-testid="ops-email-cc" />

        <label className="mt-3 block text-xs font-medium text-slate-500">Bcc</label>
        <Input value={bcc} onChange={(e) => setBcc(e.target.value)} className="mt-1 h-9 text-sm"
               data-testid="ops-email-bcc" />

        <label className="mt-3 block text-xs font-medium text-slate-500">Subject</label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)}
               className="mt-1 h-9 text-sm" data-testid="ops-email-subject" />

        <label className="mt-3 block text-xs font-medium text-slate-500">Message</label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12}
                  className="mt-1 text-sm" data-testid="ops-email-body" />

        {(draft.attachments || []).length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600"
               data-testid="ops-email-attachments">
            <Paperclip className="h-3.5 w-3.5" />
            {draft.attachments.map((a, i) => (
              <span key={i} className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5">
                {a.filename || a.name || "attachment"}
              </span>
            ))}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="ops-email-cancel">
            Cancel
          </Button>
          <Button variant="data" size="sm" onClick={send} disabled={busy} data-testid="ops-email-send">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              : <Send className="mr-1.5 h-3.5 w-3.5" />} Send
          </Button>
        </div>
      </div>
    </div>
  );
}
